import { zodResolver } from '@hookform/resolvers/zod';
import {
  type Client,
  type ClientUpdateInput,
  Client as ClientSchema,
} from '@uit/shared/schemas/client';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { apiMutation, useApi } from '../lib/api.js';
import s from './ClientEditPage.module.scss';

// Form-local schema. Differs from the shared ClientUpdateInput in one
// important way: HTML inputs always have string values, never null/undefined,
// so the optional contract fields are allowed to be ''. We convert '' → null
// in formValuesToUpdate before sending to the API (where the strict shared
// schema applies).
const FormSchema = z.object({
  name: z.string().min(1, 'Required').max(100),
  legal_name: z.string().min(1, 'Required').max(200),
  project_or_product_name: z.string().min(1, 'Required').max(200),
  contract_client_name: z.string().max(200),
  contract_description_of_work: z.string().max(2000),
  contract_work_location: z.string().max(400),
  contract_work_site_address: z.string().max(1000),
  billing_in_scope: z.boolean(),
  active: z.boolean(),
});

type FormValues = z.infer<typeof FormSchema>;

function clientToFormValues(c: Client): FormValues {
  return {
    name: c.name,
    legal_name: c.legal_name,
    project_or_product_name: c.project_or_product_name,
    contract_client_name: c.contract_client_name ?? '',
    contract_description_of_work: c.contract_description_of_work ?? '',
    contract_work_location: c.contract_work_location ?? '',
    contract_work_site_address: c.contract_work_site_address ?? '',
    billing_in_scope: c.billing_in_scope,
    active: c.active,
  };
}

function formValuesToUpdate(v: FormValues): ClientUpdateInput {
  // Empty strings become null for nullable contract fields.
  const nullable = (s: string): string | null => (s.trim() === '' ? null : s);
  return {
    name: v.name,
    legal_name: v.legal_name,
    project_or_product_name: v.project_or_product_name,
    contract_client_name: nullable(v.contract_client_name),
    contract_description_of_work: nullable(v.contract_description_of_work),
    contract_work_location: nullable(v.contract_work_location),
    contract_work_site_address: nullable(v.contract_work_site_address),
    billing_in_scope: v.billing_in_scope,
    active: v.active,
  };
}

export function ClientEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, error, loading } = useApi<Client>(id ? `/api/clients/${id}` : null, ClientSchema);

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
  });

  useEffect(() => {
    if (data) reset(clientToFormValues(data));
  }, [data, reset]);

  if (loading && !data) return <div className={s.loading}>Loading client…</div>;
  if (error || !data) {
    const is404 = error?.message.includes('404');
    return (
      <div className={s.notFound}>
        {is404 ? 'Client not found.' : `Failed to load: ${error?.message ?? 'unknown error'}`}
      </div>
    );
  }

  const onSubmit = async (values: FormValues): Promise<void> => {
    if (!id) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await apiMutation<ClientUpdateInput, Client>(
        `/api/clients/${id}`,
        formValuesToUpdate(values),
        { method: 'PATCH', schema: ClientSchema },
      );
      navigate('/clients');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (message.includes('409')) {
        setSubmitError('A client with that name already exists.');
      } else if (message.includes('404')) {
        setSubmitError('This client no longer exists.');
      } else {
        setSubmitError('Could not save changes. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={s.page}>
      <h1 className={s.title}>Edit client</h1>
      <p className={s.subtitle}>{data.name}</p>

      <form
        onSubmit={(e) => {
          void handleSubmit(onSubmit)(e);
        }}
        noValidate
      >
        {submitError && (
          <div className={s.formError} role="alert">
            {submitError}
          </div>
        )}

        <section className={s.section}>
          <h2 className={s.sectionTitle}>Identity</h2>

          <div className={s.field}>
            <label className={s.label} htmlFor="name">
              Name
            </label>
            <input
              id="name"
              type="text"
              className={s.input}
              aria-invalid={!!errors.name}
              {...register('name')}
            />
            {errors.name && <div className={s.fieldError}>{errors.name.message}</div>}
          </div>

          <div className={s.field}>
            <label className={s.label} htmlFor="legal_name">
              Legal name
            </label>
            <input
              id="legal_name"
              type="text"
              className={s.input}
              aria-invalid={!!errors.legal_name}
              {...register('legal_name')}
            />
            {errors.legal_name && <div className={s.fieldError}>{errors.legal_name.message}</div>}
          </div>

          <div className={s.field}>
            <label className={s.label} htmlFor="project_or_product_name">
              Project / product name
            </label>
            <input
              id="project_or_product_name"
              type="text"
              className={s.input}
              aria-invalid={!!errors.project_or_product_name}
              {...register('project_or_product_name')}
            />
            {errors.project_or_product_name && (
              <div className={s.fieldError}>{errors.project_or_product_name.message}</div>
            )}
          </div>

          <div className={s.field}>
            <label className={s.toggle} htmlFor="active">
              <input id="active" type="checkbox" {...register('active')} />
              Active
            </label>
          </div>

          <div className={s.field}>
            <label className={s.toggle} htmlFor="billing_in_scope">
              <input
                id="billing_in_scope"
                type="checkbox"
                {...register('billing_in_scope')}
              />
              Include this client in billing runs
            </label>
          </div>
        </section>

        <section className={s.section}>
          <h2 className={s.sectionTitle}>Contract fields</h2>
          <p className={s.sectionSubtitle}>
            Entered once per client and merged onto every contract. Leave blank to fill in later.
          </p>

          <div className={s.field}>
            <label className={s.label} htmlFor="contract_client_name">
              Client name
            </label>
            <input
              id="contract_client_name"
              type="text"
              className={s.input}
              aria-invalid={!!errors.contract_client_name}
              {...register('contract_client_name')}
            />
            {errors.contract_client_name && (
              <div className={s.fieldError}>{errors.contract_client_name.message}</div>
            )}
          </div>

          <div className={s.field}>
            <label className={s.label} htmlFor="contract_description_of_work">
              Description of work
            </label>
            <textarea
              id="contract_description_of_work"
              rows={3}
              className={s.input}
              aria-invalid={!!errors.contract_description_of_work}
              {...register('contract_description_of_work')}
            />
            {errors.contract_description_of_work && (
              <div className={s.fieldError}>{errors.contract_description_of_work.message}</div>
            )}
          </div>

          <div className={s.field}>
            <label className={s.label} htmlFor="contract_work_location">
              Work location
            </label>
            <input
              id="contract_work_location"
              type="text"
              className={s.input}
              aria-invalid={!!errors.contract_work_location}
              {...register('contract_work_location')}
            />
            {errors.contract_work_location && (
              <div className={s.fieldError}>{errors.contract_work_location.message}</div>
            )}
          </div>

          <div className={s.field}>
            <label className={s.label} htmlFor="contract_work_site_address">
              Work-site address
            </label>
            <textarea
              id="contract_work_site_address"
              rows={2}
              className={s.input}
              aria-invalid={!!errors.contract_work_site_address}
              {...register('contract_work_site_address')}
            />
            {errors.contract_work_site_address && (
              <div className={s.fieldError}>{errors.contract_work_site_address.message}</div>
            )}
          </div>
        </section>

        <div className={s.actions}>
          <Link to="/clients" className={s.cancel}>
            Cancel
          </Link>
          <button type="submit" className={s.save} disabled={submitting}>
            {submitting ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
